/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/FeaturedProducts/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import styles from "./index.module.css";

import { CircularProgress, Backdrop, SvgIcon } from "@mui/material";
import { ReactComponent as SearchIcon } from "../../assets/icons/Search.svg";
import { ReactComponent as MedalIcon } from "../../assets/icons/FeaturedSectors/Medal.svg";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { getFeaturedProducts } from "../../logic/featuredProducts";
import { useTranslation } from "react-i18next";

const FeaturedProducts = ({ handleSearchAgain }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const products = await getFeaturedProducts(navigate);
        setFeaturedProducts(Array.isArray(products) ? products : []);
      } catch (error) {
        logger.error("Error fetching featured products:", {
          err: sanitizeError(error),
        });
        setError(t("components.featuredSections.errorLoadingProducts"));
        setFeaturedProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [navigate]);

  const handleProductSearch = (product) => {
    logger.info("Product: ", { data: product });
    logger.info("Product name: ", { data: product[i18n.language].trim() });
    handleSearchAgain(null, product[i18n.language].trim(), "product");
  };

  if (loading) {
    return (
      <div className={styles.featuredSection}>
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          }}
          open={loading}
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: "#4eabe7",
            }}
          />
        </Backdrop>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.featuredSection}>
        <div className={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.featuredSection}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <MedalIcon className={styles.titleIcon} />
          <h2 className={styles.titleText}>
            {t("components.featuredProducts.header")}
          </h2>
        </div>
        <span className={styles.subtitle}>
          {t("components.featuredProducts.subtitle")}
        </span>
        <hr className={styles.divider} />
      </div>
      {featuredProducts.length === 0 && (
        <div className={styles.noResults}>
          <p>{t("components.featuredProducts.noResults")}</p>
        </div>
      )}
      <div className={styles.productsList}>
        {featuredProducts.length > 0 &&
          featuredProducts.map((product, index) => (
            <div key={index} className={styles.productCard}>
              <div className={styles.productInfo}>
                <div className={styles.productHeader}>
                  <div className={styles.productTitleContainer}>
                    <span className={styles.productName}>
                      {product[i18n.language]}
                    </span>
                  </div>
                </div>
                <hr className={styles.productDivider} />
                <div
                  className={styles.searchAgainContainer}
                  onClick={() => handleProductSearch(product)}
                >
                  <div className={styles.iconContainer}>
                    <SvgIcon className={styles.icon} component={SearchIcon} />
                  </div>
                  <span>{t("components.featuredProducts.searchAgain")}</span>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default FeaturedProducts;
